import React, { useRef, useState, useEffect, useCallback } from 'react';
import Scene from './components/Scene';
import Terminal from './components/Terminal';
import FileViewer from './components/FileViewer';
import ProjectsPage from './components/ProjectsPage';
import { DIRECTORY_PAGES } from './data/portfolio';
import './App.css';

const DEFAULT_VIEW = 'home';

function getViewFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');
  return DIRECTORY_PAGES[view] ? view : DEFAULT_VIEW;
}

function getPublicAssetPath(path) {
  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export default function App() {
  const mouse = useRef({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [typedName, setTypedName] = useState('');
  const fullName = 'SAGAR';
  const [openFile, setOpenFile] = useState(null);
  const [activeView, setActiveView] = useState(getViewFromUrl);
  const [isMobileLayout, setIsMobileLayout] = useState(() => (
    window.matchMedia?.('(max-width: 700px)').matches || false
  ));

  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('portfolio-theme');
      if (saved) return saved === 'dark';
      return false; // default to light mode
    } catch {
      return false;
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(max-width: 700px)');
    if (!mediaQuery) return undefined;

    const handleChange = (event) => setIsMobileLayout(event.matches);
    setIsMobileLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      try { localStorage.setItem('portfolio-theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  }, []);

  const setThemeDirectly = useCallback((dark) => {
    setIsDark(dark);
    try { localStorage.setItem('portfolio-theme', dark ? 'dark' : 'light'); } catch {}
  }, []);

  const openDirectoryPage = useCallback((view) => {
    if (!DIRECTORY_PAGES[view]) return;
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    url.hash = '';
    window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
    setActiveView(view);
  }, []);

  const showHome = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('view');
    window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
    setActiveView('home');
  }, []);

  const openResume = useCallback(() => {
    window.location.assign(getPublicAssetPath('/resume.pdf'));
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setActiveView(getViewFromUrl());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleMouseMove = useCallback((e) => {
    mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    setCursorPos({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    const startDelay = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        if (i < fullName.length) {
          setTypedName(fullName.slice(0, i + 1));
          i++;
        } else {
          clearInterval(interval);
        }
      }, 130);
    }, 800);
    return () => clearTimeout(startDelay);
  }, []);

  if (DIRECTORY_PAGES[activeView]) {
    return (
      <ProjectsPage
        page={DIRECTORY_PAGES[activeView]}
        isDark={isDark}
        onThemeToggle={toggleTheme}
        onHome={showHome}
      />
    );
  }

  return (
    <div className="app" onMouseMove={handleMouseMove}>
      <div
        className="cursor-glow"
        style={{ left: cursorPos.x, top: cursorPos.y }}
      />

      <nav className="navbar">
        <div className="nav-logo">S_</div>
        <div className="nav-right">
          <button className="theme-toggle" onClick={toggleTheme}>
            {isDark ? '☀' : '☾'}
          </button>
        </div>
      </nav>

      <section className="hero">
        <div className="hero-canvas">
          <Scene mouse={mouse} isDark={isDark} isMobile={isMobileLayout} />
        </div>
        <div className="hero-vignette" />

        <div className="hero-content">
          <p className="hero-greeting">Hi, I'm</p>
          <h1 className="hero-name">
            {typedName}
            <span className="cursor-blink">_</span>
          </h1>
          <p className="hero-tagline">Developer &middot; Builder &middot; Creator</p>

          <div className="mobile-quick-actions" aria-label="Quick navigation">
            <button type="button" onClick={() => openDirectoryPage('projects')}>Projects</button>
            <button type="button" onClick={() => openDirectoryPage('research')}>Research</button>
            <button type="button" onClick={() => openDirectoryPage('experience')}>Experience</button>
            <button type="button" onClick={() => openDirectoryPage('hobbies')}>Hobbies</button>
            <button type="button" onClick={openResume}>Resume</button>
            <button type="button" onClick={() => setOpenFile('contact.txt')}>Contact</button>
          </div>

          <div className="hero-terminal">
            <Terminal
              onThemeChange={setThemeDirectly}
              isDark={isDark}
              onOpenFile={setOpenFile}
              onOpenDirectory={openDirectoryPage}
            />
          </div>
        </div>
      </section>

      {openFile && (
        <FileViewer file={openFile} onClose={() => setOpenFile(null)} />
      )}
    </div>
  );
}
